import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ArrowDown, ArrowUp, Close, Search } from "@air/next-icons";
import { IconButton } from "@air/primitive-icon-button";
import { Spinner } from "@air/primitive-spinner";
import { forwardRef, memo, useCallback, useImperativeHandle, useRef, useState } from "react";
import { PDFSearchInput } from "~/components/AssetModal/Visualizer/PDFVisualizer/PDFSearch/components/PDFSearchInput";
import { usePDFSearchHotkeys } from "~/components/AssetModal/Visualizer/PDFVisualizer/PDFSearch/hooks/usePDFSearchHotkeys";
import { SEARCH_WIDTH } from "~/components/AssetModalV2/constants";
export const PDFSearch = /*#__PURE__*/ memo(/*#__PURE__*/ forwardRef((param, forwardedRef)=>{
    let { currentResult, totalResults, onSearchChange, isSearching, clearSearch, setCurrentResultIndex } = param;
    const inputRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const openSearch = useCallback((selectText)=>{
        var _inputRef_current;
        setIsExpanded(true);
        (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.focus(selectText);
    }, []);
    const closeSearch = useCallback(()=>{
        var _inputRef_current;
        setIsExpanded(false);
        (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.clear();
        clearSearch();
    }, [
        clearSearch
    ]);
    useImperativeHandle(forwardedRef, ()=>({
            setInitialValue: (value)=>{
                var _inputRef_current;
                return (_inputRef_current = inputRef.current) === null || _inputRef_current === void 0 ? void 0 : _inputRef_current.setInitialValue(value);
            },
            isOpen: ()=>isExpanded,
            openSearch: ()=>openSearch(false)
        }));
    const onShowNextResult = useCallback(()=>{
        if (currentResult < totalResults - 1) {
            setCurrentResultIndex(currentResult + 1);
        } else {
            setCurrentResultIndex(0);
        }
    }, [
        currentResult,
        setCurrentResultIndex,
        totalResults
    ]);
    const onShowPreviousResult = useCallback(()=>{
        if (currentResult > 0) {
            setCurrentResultIndex(currentResult - 1);
        } else {
            setCurrentResultIndex(totalResults - 1);
        }
    }, [
        currentResult,
        setCurrentResultIndex,
        totalResults
    ]);
    usePDFSearchHotkeys({
        openSearch: ()=>openSearch(true),
        closeSearch,
        isExpanded,
        showNextResult: onShowNextResult,
        showPreviousResult: onShowPreviousResult,
        inputRef
    });
    const handleOpenSearch = useCallback(()=>{
        openSearch(true);
    }, [
        openSearch
    ]);
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: !isExpanded ? /*#__PURE__*/ _jsx(IconButton, {
            "data-testid": "PDF_SEARCH_BUTTON",
            onClick: handleOpenSearch,
            icon: Search,
            label: "Search document",
            appearance: "ghost",
            color: "grey",
            size: "medium"
        }) : /*#__PURE__*/ _jsx("div", {
            className: "box-content overflow-hidden rounded-8 border border-grey-5 bg-grey-1 transition-all duration-300 ease-in-out",
            style: {
                width: "".concat(SEARCH_WIDTH, "px")
            },
            children: /*#__PURE__*/ _jsxs("div", {
                className: "flex size-full items-center rounded-8 pr-2",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "flex size-10 shrink-0 items-center justify-center rounded-8",
                        children: isSearching ? /*#__PURE__*/ _jsx(Spinner, {
                            className: "size-6 text-grey-9"
                        }) : /*#__PURE__*/ _jsx(Search, {
                            className: "size-6 text-grey-9"
                        })
                    }),
                    /*#__PURE__*/ _jsx(PDFSearchInput, {
                        onSearchChange: onSearchChange,
                        ref: inputRef
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex shrink-0 items-center gap-1",
                        children: [
                            !!totalResults && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "text-12 text-grey-9",
                                        "data-testid": "PDF_SEARCH_RESULTS_TOTAL",
                                        children: [
                                            currentResult + 1,
                                            " of ",
                                            totalResults.toLocaleString()
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(IconButton, {
                                        disabled: totalResults === 1,
                                        label: "Previous search result",
                                        icon: ArrowUp,
                                        appearance: "ghost",
                                        color: "grey",
                                        size: "small",
                                        onClick: onShowPreviousResult,
                                        "data-testid": "PDF_SEARCH_PREVIOUS_BUTTON"
                                    }),
                                    /*#__PURE__*/ _jsx(IconButton, {
                                        disabled: totalResults === 1,
                                        onClick: onShowNextResult,
                                        label: "Next search result",
                                        icon: ArrowDown,
                                        appearance: "ghost",
                                        color: "grey",
                                        size: "small",
                                        "data-testid": "PDF_SEARCH_NEXT_BUTTON"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(IconButton, {
                                onClick: closeSearch,
                                label: "Close document search",
                                icon: Close,
                                appearance: "ghost",
                                color: "grey",
                                size: "small",
                                "data-testid": "PDF_SEARCH_CLOSE_BUTTON"
                            })
                        ]
                    })
                ]
            })
        })
    });
}));
PDFSearch.displayName = "PDFSearch";
